import { RefreshCw, Shuffle } from "lucide-react";
import React from "react";

import type * as ozon from "@/api/ozonSupply";
import { Button } from "@/components/ui/button";
import { int0 } from "../format";
import type { PlaceEditActions } from "./useOzonDocActions";

/** Над таблицей мест — только то, что описывает САМУ раскладку: чем везём и что вышло.
 *
 *  ⚠️ Кнопки отсюда ушли наверх, к стадии («Разложить», «Отправить места», «Записать в 1С» и
 *  остальное — `actionsFor`): их было до десяти в ряд, и нужная терялась среди аварийных.
 *  Здесь остались два селекта, и только пока не выдан ни один код: после них тип поставки
 *  площадка не переключает, а стратегия ничего уже не решает.
 */
export function PlacesToolbar({
  places,
  coded,
  data,
  transport,
  strategy,
  setStrategy,
  busy,
  fetching,
  edits,
  changeMode = false,
}: {
  places: ozon.SavedPlace[];
  /** сколько непустых мест уже с кодом */
  coded: number;
  data: ozon.OzonPlaces | undefined;
  transport: boolean;
  strategy: string;
  setStrategy: (v: string) => void;
  busy: boolean;
  fetching: boolean;
  edits: PlaceEditActions;
  /** идёт правка состава: места с кодами правятся ПО КОРОБАМ, а коды снимает каскад */
  changeMode?: boolean;
}): React.ReactElement {
  return (
    <div className="mb-1.5 flex flex-wrap items-center gap-2">
      {/* ⚠️ ГЛАВНАЯ кнопка режима над таблицей: партии подбирались в день раскладки, а товар с тех
          пор мог уйти — и вскрывается это «не хватает товара» на записи в 1С, когда коды уже
          выданы. Кнопка сверяет раскладку с ЖИВЫМ остатком и трогает только негодные строки:
          места, чьи партии на складе есть, остаются со своими кодами и этикетками. */}
      {changeMode && (
        <Button
          size="sm"
          variant="default"
          disabled={busy}
          onClick={() => edits.repick.mutate()}
          title="Спросить у 1С свободный остаток, оставить годные строки как есть и переподобрать только те, чьей партии на складе больше нет. У мест с кодами переподбираются только негодные строки: их коробы снимаются, годные коробы остаются со своими кодами и наклейками."
        >
          <Shuffle
            className={
              edits.repick.isPending ? "size-3.5 animate-spin" : "size-3.5"
            }
          />
          Перераспределить партии
          <span className="font-normal opacity-80">
            · сверить с 1С и переподобрать нехватку
          </span>
        </Button>
      )}
      {coded === 0 && (
        <>
          {/* ⚠️ ТИП ПОСТАВКИ — первым, до раскладки: он решает, какие раскладки вообще
                существуют. У площадки это разные режимы, и переключатель транспортных
                грузомест включается только ДО грузомест, назад не отыгрывается. */}
          <select
            value={transport ? "transport" : "plain"}
            disabled={busy}
            onChange={(e) => {
              const on = e.target.value === "transport";
              if (
                places.length > 0 &&
                !window.confirm(
                  "Сменить тип поставки? Раскладка будет сброшена — правила сборки у режимов разные.",
                )
              ) {
                // ⚠️ вернуть селект к настоящему режиму: без этого в DOM оставался выбранный,
                // но НЕ применённый вариант — экран противоречил сам себе
                e.currentTarget.value = transport ? "transport" : "plain";
                return;
              }
              edits.mode.mutate(on);
            }}
            className="h-8 rounded border border-input bg-background px-2 font-medium"
            title="С грузоместами — паллеты уезжают транспортными местами, и площадка требует ставить на них ВСЕ коробки. Обычная — как раньше."
          >
            <option value="plain">обычная поставка</option>
            <option value="transport">с грузоместами (паллеты)</option>
          </select>
          {/* в режиме грузомест выбирать нечего: коробочной и частичной раскладки там нет */}
          {!transport && (
            <select
              value={strategy}
              disabled={busy}
              onChange={(e) => setStrategy(e.target.value)}
              className="h-8 rounded border border-input bg-background px-2"
              title="Чем везти. «По правилу» — как решает бот: от 12 коробов на поставку паллета оправдана, меньше — коробами."
            >
              <option value="">по правилу (от 12 коробов — паллеты)</option>
              <option value="PALLET">паллетами</option>
              <option value="BOX">коробами</option>
              <option value="COMBO">паллеты + остаток коробами</option>
            </select>
          )}
        </>
      )}
      {coded > 0 && (
        <span className="text-amber-600 dark:text-amber-400">
          на площадке {coded} из{" "}
          {places.filter((p) => p.items.length > 0).length} мест —{" "}
          {/* ⚠️ В режиме изменений «через снятие кодов» — уже неправда: коды снимает каскад, а
              человек правит строки. Атом правки при этом КОРОБ, а не место: у места остаются его
              номер, паллета и уцелевшие коробы со своими наклейками — «выносится целиком» звало
              бы переклеивать всю паллету из-за одного короба */}
          {changeMode
            ? "места с кодами правятся по коробам: снятые коды и досылку применит правка"
            : "правка через снятие кодов"}
        </span>
      )}
      <span className="ml-auto font-medium">{summary(places)}</span>
      {/* ⚠️ Лимиты площадки — её собственные числа по ЭТОЙ заявке, а не наши догадки. Ради них
            и переехали на v2-ручку: пока их не показывали, «паллетами сюда нельзя» вскрывалось
            счётом за палеты-микс (14.09.2026 — 29 562 ₽). */}
      {data && Object.keys(data.limits ?? {}).length > 0 && (
        <span
          className="text-muted-foreground"
          title="Лимиты площадки по этой заявке"
        >
          {/* ⚠️ Пока режим не включён, эти числа ещё не про эту поставку: включение
                транспортных грузомест меняет их (замер: 30 коробов → 1 500). Показывать их
                как приговор значит пугать цифрой, которая сейчас же и сменится. */}
          {transport && !data.limits.max_transport_pallet_count ? (
            "площадка: лимиты сменятся при включении грузомест"
          ) : (
            <>
              площадка: коробов до {int0(data.limits.max_box_count ?? 0)}
              {data.limits.max_transport_pallet_count
                ? ` · паллет до ${int0(data.limits.max_transport_pallet_count)}`
                : " · без паллет"}
            </>
          )}
        </span>
      )}
      {fetching && (
        <RefreshCw className="size-3.5 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}

/** Чем в итоге едем — одной строкой. «Паллеты или короба?» не должно требовать пересчёта строк
 *  глазами: стратегию выбирает автомат, и результат надо назвать. */
function summary(places: ozon.SavedPlace[]): string {
  if (places.length === 0) return "";
  const pallets = places.filter((p) => p.kind === "PALLET").length;
  const boxes = places.length - pallets;
  const qty = places.reduce((s, p) => s + p.qty, 0);
  const inBoxes = places.reduce<number | null>(
    (s, p) => (s === null || p.boxes === null ? null : s + p.boxes),
    0,
  );
  const parts = [
    pallets > 0 ? `паллет ${pallets}` : "",
    boxes > 0 ? `коробов ${boxes}` : "",
  ].filter(Boolean);
  return `${parts.join(" + ")} · ${int0(qty)} шт${inBoxes === null ? "" : ` · ${int0(inBoxes)} кор`}`;
}
